/**
 * @internal true
 * @module master/MasterFeedbackPage
 * Painel de Feedback do Admin Master — Refatorado com Kanban Premium,
 * Governança em 3 Níveis, Tags dinâmicas e Métricas estendidas.
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquarePlus, Search, Filter, CheckCircle2, Clock, AlertTriangle,
  ChevronDown, Send, X, Bug, Lightbulb, HelpCircle, Eye, Loader2,
  Tag, UserPlus, Calendar, Plus, RefreshCw, MessageSquare, ArrowRight,
  User, CheckSquare, AlertCircle, PlayCircle, Check,
  Settings, Link, Unlink, ExternalLink, ShieldCheck, Activity, Download
} from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { ACHierarchySelector } from '../../components/ui/ACHierarchySelector';
import { ACMultiSelectDropdown, type ACMultiSelectOption } from '../../components/ui/ACMultiSelectDropdown';
import { KanbanBoard } from '../../components/ui/KanbanBoard';

import '../../components/ui/datatable.css';

const TIPO_ICONS: Record<string, typeof Bug> = {
  PROBLEMA: Bug,
  SUGESTAO: Lightbulb,
  DUVIDA: HelpCircle,
};

const TIPO_LABELS: Record<string, string> = {
  PROBLEMA: 'Problema',
  SUGESTAO: 'Sugestão',
  DUVIDA: 'Dúvida',
};

const PRIORIDADE_COLORS: Record<string, { text: string; bg: string; border: string }> = {
  CRITICA: { text: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.2)' },
  ALTA: { text: '#f97316', bg: 'rgba(249, 115, 22, 0.12)', border: 'rgba(249, 115, 22, 0.2)' },
  MEDIA: { text: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.2)' },
  BAIXA: { text: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.15)' },
};

const COLUMNS = [
  { id: 'ABERTO', label: 'Aberto', color: '#f59e0b', bg: '#f59e0b12' },
  { id: 'TRIAGEM', label: 'Triagem', color: '#8b5cf6', bg: '#8b5cf612' },
  { id: 'EM_ANALISE', label: 'Em Análise', color: '#3b82f6', bg: '#3b82f612' },
  { id: 'PENDENTE', label: 'Pendente', color: '#ec4899', bg: '#ec489912' },
  { id: 'EM_CORRECAO', label: 'Em Correção', color: '#ef4444', bg: '#ef444412' },
  { id: 'EM_TESTE', label: 'Em Teste', color: '#06b6d4', bg: '#06b6d412' },
  { id: 'RESOLVIDO', label: 'Resolvido', color: '#10b981', bg: '#10b98112' },
  { id: 'FECHADO', label: 'Fechado', color: '#6b7280', bg: '#6b728012' },
];

const PERFIL_OPTIONS: ACMultiSelectOption[] = [
  { id: 'TODOS', nome: 'Todos Perfis' },
  { id: 'ADMIN', nome: 'Administrador' },
  { id: 'MUNICIPAL', nome: 'Gestor Municipal' },
  { id: 'ESTADUAL', nome: 'Gestor Estadual' },
  { id: 'REDE', nome: 'Gestor de Rede' },
  { id: 'ESCOLA', nome: 'Diretor / Escola' },
  { id: 'PROFESSOR', nome: 'Professor' },
  { id: 'RESPONSAVEL', nome: 'Responsável' },
  { id: 'ALUNO', nome: 'Aluno' },
];

interface Feedback {
  id: string;
  tipo: string;
  titulo: string;
  descricao: string;
  rota: string;
  screenshot?: string;
  prioridade: string;
  status: string;
  resposta?: string;
  respondidoPor?: string;
  respondidoEm?: string;
  criadoEm: string;
  usuarioId: string;
  escolaId?: string | null;
  usuario?: {
    id: string;
    nome: string;
    perfil: string;
    email?: string;
    fotoUrl?: string | null;
    escolaId?: string | null;
    municipioId?: string | null;
    estadoId?: string | null;
    redeId?: string | null;
  };
  triadoPorId?: string | null;
  triadoPor?: { id: string; nome: string; perfil: string; email?: string; fotoUrl?: string | null } | null;
  analisadoPorId?: string | null;
  analisadoPor?: { id: string; nome: string; perfil: string; email?: string; fotoUrl?: string | null } | null;
  tags?: Array<{ tag: { id: string; nome: string; cor: string } }>;
  comentarios?: Array<{
    id: string;
    conteudo: string;
    criadoEm: string;
    autor?: { id: string; nome: string; perfil: string; email?: string; fotoUrl?: string | null };
  }>;
  anexos?: Array<{
    id: string;
    nome: string;
    url: string;
    tipo: string;
    tamanhoBytes?: number;
    criadoEm: string;
    enviadoPor?: { id: string; nome: string; perfil: string };
  }>;
  historico?: Array<{
    id: string;
    acao: string;
    descricao: string;
    criadoEm: string;
    usuario?: { id: string; nome: string; perfil: string };
  }>;
}

interface TagData {
  id: string;
  nome: string;
  cor: string;
  status?: string | null;
  grupo?: string | null;
  prioridade?: string | null;
}

interface TeamUser {
  id: string;
  nome: string;
  email: string;
  perfil: string;
  cargo?: string | null;
}

function formatDescricao(descricao: string) {
  if (!descricao) return null;
  
  const hasLegenda = descricao.includes('[Legenda Extraída pela IA]:');
  const hasAnalise = descricao.includes('[Análise IA Alice]:');
  
  if (!hasLegenda && !hasAnalise) {
    return <p style={{ fontSize: '.84rem', color: 'var(--text-main)', lineHeight: 1.6, margin: '14px 0', whiteSpace: 'pre-wrap' }}>{descricao}</p>;
  }
  
  let legendaText = '';
  let analiseText = '';
  let restText = '';
  
  const parts = descricao.split('\n\n');
  parts.forEach(part => {
    if (part.startsWith('[Legenda Extraída pela IA]:')) {
      legendaText = part.replace('[Legenda Extraída pela IA]:', '').trim();
    } else if (part.startsWith('[Análise IA Alice]:')) {
      analiseText = part.replace('[Análise IA Alice]:', '').trim();
    } else {
      restText += (restText ? '\n\n' : '') + part;
    }
  });
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '14px 0' }}>
      {restText && (
        <p style={{ fontSize: '.84rem', color: 'var(--text-main)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
          {restText}
        </p>
      )}
      
      {legendaText && (
        <div style={{
          background: 'var(--bg-card, #f8fafc)',
          borderLeft: '4px solid #7c3aed',
          borderRadius: '4px 12px 12px 12px',
          padding: '12px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '.72rem', fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>📝</span> Legenda Extraída (Áudio do Usuário)
          </div>
          <p style={{ fontSize: '.82rem', color: 'var(--text-main)', margin: 0, fontStyle: 'italic', lineHeight: 1.5 }}>
            {legendaText}
          </p>
        </div>
      )}
      
      {analiseText && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%)',
          border: '1px solid rgba(56, 189, 248, 0.2)',
          borderRadius: 12,
          padding: '12px 16px',
          boxShadow: '0 2px 8px rgba(37, 99, 235, 0.03)'
        }}>
          <div style={{ fontSize: '.72rem', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>✨</span> Análise Inteligente IA Alice
          </div>
          <p style={{ fontSize: '.82rem', color: 'var(--text-main)', margin: 0, lineHeight: 1.5 }}>
            {analiseText}
          </p>
        </div>
      )}
    </div>
  );
}

interface ACTagsDropdownWithCRUDProps {
  tags: TagData[];
  selectedTagIds: string[];
  onChange: (ids: string[]) => void;
  onCreateTag: (nome: string, cor: string, grupo: string) => Promise<void>;
  onDeleteTag: (id: string) => Promise<void>;
}

function ACTagsDropdownWithCRUD({
  tags,
  selectedTagIds,
  onChange,
  onCreateTag,
  onDeleteTag
}: ACTagsDropdownWithCRUDProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState('#8b5cf6');
  const [grupo, setGrupo] = useState('');
  const [criando, setCriando] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleToggle = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId];
    onChange(next);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) return;
    setCriando(true);
    await onCreateTag(nome.trim(), cor, grupo.trim());
    setNome('');
    setGrupo('');
    setCriando(false);
  };

  const filtered = tags.filter(t => t.nome.toLowerCase().includes(search.toLowerCase()));

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  return (
    <div className="dd-wrapper" ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <button
        type="button"
        className={`dd-trigger ${isOpen ? 'dd-trigger-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: 38,
          borderRadius: 10,
          border: '1px solid var(--border-color, #e2e8f0)',
          background: 'var(--bg-card, #fff)',
          color: 'var(--text-main, #0f172a)',
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          width: '100%',
          outline: 'none',
        }}
      >
        <span style={{ fontSize: '0.78rem', fontWeight: selectedTagIds.length > 0 ? 700 : 400 }}>
          {selectedTagIds.length === 0 ? 'Todas as Tags' : `${selectedTagIds.length} selecionada(s)`}
        </span>
        <ChevronDown size={14} style={{ color: 'var(--text-muted, #94a3b8)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            width: 320,
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: 12,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            zIndex: 999,
            padding: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {/* Header & Search */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.74rem', fontWeight: 800 }}>Tags ({filtered.length})</span>
            {selectedTagIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Limpar
              </button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              ref={searchRef}
              type="text"
              placeholder="Pesquisar tag..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input"
              style={{ padding: '4px 8px 4px 26px', fontSize: '0.72rem', borderRadius: 6 }}
            />
          </div>

          {/* Scrollable list with delete action */}
          <div style={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }} className="custom-scroll">
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                Nenhuma tag encontrada
              </div>
            ) : (
              filtered.map(tg => {
                const isSelected = selectedTagIds.includes(tg.id);
                return (
                  <div
                    key={tg.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '4px 6px',
                      borderRadius: 6,
                      background: isSelected ? 'var(--bg-body, rgba(0,0,0,0.02))' : 'transparent',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggle(tg.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'left'
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          border: '1.5px solid var(--border-color, #cbd5e1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isSelected ? '#8b5cf6' : 'transparent',
                          borderColor: isSelected ? '#8b5cf6' : 'var(--border-color, #cbd5e1)',
                          color: 'white',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && <Check size={8} strokeWidth={3} />}
                      </div>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: tg.cor, flexShrink: 0 }} />
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          color: 'var(--text-main)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tg.nome} {tg.grupo && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.62rem' }}>({tg.grupo})</span>}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteTag(tg.id)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', padding: 2, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      title="Excluir tag"
                    >
                      <X size={10} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Quick Tag Creator Form inside dropdown */}
          <form
            onSubmit={handleCreate}
            style={{
              borderTop: '1px solid var(--border-color, #e2e8f0)',
              paddingTop: 8,
              marginTop: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}
          >
            <span style={{ fontSize: '0.66rem', fontWeight: 800, color: 'var(--text-muted)' }}>Nova Tag Rápida</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                required
                placeholder="Nome da tag..."
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="form-input"
                style={{ fontSize: '0.68rem', padding: '4px 6px', flex: 1 }}
              />
              <input
                type="color"
                value={cor}
                onChange={(e) => setCor(e.target.value)}
                style={{ width: 24, height: 24, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                placeholder="Grupo (opcional)..."
                value={grupo}
                onChange={(e) => setGrupo(e.target.value)}
                className="form-input"
                style={{ fontSize: '0.68rem', padding: '4px 6px', flex: 1 }}
              />
              <button
                type="submit"
                disabled={criando || !nome.trim()}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#8b5cf6',
                  color: 'white',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  opacity: criando || !nome.trim() ? 0.6 : 1
                }}
              >
                Criar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export function MasterFeedbackPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  // States
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [usuarios, setUsuarios] = useState<TeamUser[]>([]);
  const [tags, setTags] = useState<TagData[]>([]);
  const [loading, setLoading] = useState(true);


  // Hierarchy Lists States
  const [redes, setRedes] = useState<any[]>([]);
  const [estados, setEstados] = useState<any[]>([]);
  const [municipios, setMunicipios] = useState<any[]>([]);
  const [escolas, setEscolas] = useState<any[]>([]);

  // Delegation and Presence States
  const [responsabilidades, setResponsabilidades] = useState<any[]>([]);
  const [presencas, setPresencas] = useState<Record<string, { lastActive: string; rota: string; status: 'online' | 'ocioso' }>>({});
  const [colunaDelegando, setColunaDelegando] = useState<string | null>(null);
  const [buscaDelegar, setBuscaDelegar] = useState<string>('');

  // Filters state (with persistence)

  const [filtroStatuses, setFiltroStatuses] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_statuses');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [filtroTipo, setFiltroTipo] = useState<string>('TODOS');
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>('TODAS');
  const [filtroUsuarioIds, setFiltroUsuarioIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_usuarios');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [filtroTagIds, setFiltroTagIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_tag_ids');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [busca, setBusca] = useState('');

  // Expanded Filters (with persistence)
  const [filtroPerfil, setFiltroPerfil] = useState<string>('ADMIN');
  const [filtroRedes, setFiltroRedes] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_redes');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [filtroEstados, setFiltroEstados] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_estados');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [filtroMunicipios, setFiltroMunicipios] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_municipios');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [filtroEscolas, setFiltroEscolas] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('master_feedback_filtro_escolas');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Modals state
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [resposta, setResposta] = useState('');
  const [respondendo, setRespondendo] = useState(false);

  // Trello Parity States
  const [novoComentario, setNovoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [novoAnexoNome, setNovoAnexoNome] = useState('');
  const [novoAnexoUrl, setNovoAnexoUrl] = useState('');
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  const [activeTabRight, setActiveTabRight] = useState<'comentarios' | 'historico'>('comentarios');

  // Screenshot/Video zooms
  const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);

  // Separate 2-Modal System
  const [modalServerIntegrationOpen, setModalServerIntegrationOpen] = useState(false);
  const [modalUserSettingsOpen, setModalUserSettingsOpen] = useState(false);
  const [trelloTab, setTrelloTab] = useState<'webhooks' | 'logs'>('webhooks');
  const [trelloAccount, setTrelloAccount] = useState(() => {
    const savedToken = localStorage.getItem('ac_trello_operator_key');
    if (savedToken) {
      return {
        conectado: true,
        nome: 'Euler Magalhães Junior',
        username: '@eulermagalhaesjunior',
        avatarUrl: 'https://trello-members.s3.amazonaws.com/5cf1548212b502197ce22091/f62cc86de9becdb95a85bc40675c84ce/170.png',
        trelloId: '5cf1548212b502197ce22091',
        ambiente: 'DESENVOLVIMENTO (duEuler)'
      };
    }
    return {
      conectado: false,
      nome: 'Não Conectado',
      username: '@desconectado',
      avatarUrl: '',
      trelloId: '',
      ambiente: 'DESENVOLVIMENTO'
    };
  });
  const [senhaServidorInput, setSenhaServidorInput] = useState('');
  const [senhaServidorAutorizada, setSenhaServidorAutorizada] = useState(false);

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiSecretInput, setApiSecretInput] = useState('');
  const [userTokenInput, setUserTokenInput] = useState('');
  const [webhookUrlInput, setWebhookUrlInput] = useState('https://alunoconectado.com.br/api/v1/feedback/trello/webhook');
  const [testandoWebhook, setTestandoWebhook] = useState(false);

  // Handlers para Comentários e Anexos no estilo Trello
  async function enviarNovoComentario(feedbackId: string) {
    if (!novoComentario.trim()) return;
    setEnviandoComentario(true);
    try {
      const res = await api.post(`/feedback/${feedbackId}/comentarios`, { conteudo: novoComentario.trim() });
      if (res.data.ok) {
        toast.success('Comentário publicado no cartão!');
        setNovoComentario('');
        // Atualiza cartão selecionado e lista principal
        const updatedComentarios = await api.get(`/feedback/${feedbackId}/comentarios`);
        setSelectedFeedback((prev) => prev ? { ...prev, comentarios: updatedComentarios.data.comentarios } : null);
        carregarDadosSilencioso();
      }
    } catch {
      toast.error('Erro ao publicar comentário');
    } finally {
      setEnviandoComentario(false);
    }
  }

  async function enviarNovoAnexo(feedbackId: string) {
    if (!novoAnexoNome.trim() || !novoAnexoUrl.trim()) return;
    setEnviandoAnexo(true);
    try {
      const isMedia = novoAnexoUrl.match(/\.(png|jpg|jpeg|gif|webm|mp4)$/i);
      const tipo = isMedia ? (novoAnexoUrl.match(/\.(webm|mp4)$/i) ? 'VIDEO' : 'IMAGE') : 'DOCUMENT';
      const res = await api.post(`/feedback/${feedbackId}/anexos`, {
        nome: novoAnexoNome.trim(),
        url: novoAnexoUrl.trim(),
        tipo
      });
      if (res.data.ok) {
        toast.success('Anexo adicionado ao cartão!');
        setNovoAnexoNome('');
        setNovoAnexoUrl('');
        const updatedAnexos = await api.get(`/feedback/${feedbackId}/anexos`);
        setSelectedFeedback((prev) => prev ? { ...prev, anexos: updatedAnexos.data.anexos } : null);
        carregarDadosSilencioso();
      }
    } catch {
      toast.error('Erro ao adicionar anexo');
    } finally {
      setEnviandoAnexo(false);
    }
  }

  async function carregarDadosSilencioso() {
    try {
      const fbRes = await api.get('/feedback/admin');
      setFeedbacks(fbRes.data.feedbacks || []);
      setResponsabilidades(fbRes.data.responsabilidades || []);
      setPresencas(fbRes.data.presencas || {});
    } catch (e) {
      console.warn('Erro ao atualizar silenciosamente', e);
    }
  }

  // Drag and Drop state (handled internally by KanbanBoard, no local draggedId needed)

  // Load Data
  async function carregarDados() {
    setLoading(true);
    try {
      const [fbRes, userRes, tagsRes, redesRes, estadosRes, municipiosRes, escolasRes] = await Promise.all([
        api.get('/feedback/admin'),
        api.get('/admin/usuarios'),
        api.get('/feedback/tags'),
        api.get('/admin/redes'),
        api.get('/admin/estados'),
        api.get('/admin/municipios'),
        api.get('/admin/escolas')
      ]);
      setFeedbacks(fbRes.data.feedbacks || []);
      setResponsabilidades(fbRes.data.responsabilidades || []);
      setPresencas(fbRes.data.presencas || {});
      setUsuarios(userRes.data || []);
      setTags(tagsRes.data.tags || []);
      setRedes(redesRes.data || []);
      setEstados(estadosRes.data || []);
      setMunicipios(municipiosRes.data || []);
      setEscolas(escolasRes.data || []);
    } catch (e: any) {
      toast.error('Erro ao carregar Central de Feedbacks', e);
    }
    setLoading(false);
  }

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    // Poll every 10s for presence/delegation updates
    const interval = setInterval(() => {
      api.get('/feedback/admin').then((fbRes) => {
        setFeedbacks(fbRes.data.feedbacks || []);
        setResponsabilidades(fbRes.data.responsabilidades || []);
        setPresencas(fbRes.data.presencas || {});
      }).catch(err => console.warn('Erro ao atualizar presença/responsabilidades', err));
    }, 10000);

    return () => clearInterval(interval);
  }, []);



  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_perfil', filtroPerfil);
  }, [filtroPerfil]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_redes', JSON.stringify(filtroRedes));
  }, [filtroRedes]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_estados', JSON.stringify(filtroEstados));
  }, [filtroEstados]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_municipios', JSON.stringify(filtroMunicipios));
  }, [filtroMunicipios]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_escolas', JSON.stringify(filtroEscolas));
  }, [filtroEscolas]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_statuses', JSON.stringify(filtroStatuses));
  }, [filtroStatuses]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_usuarios', JSON.stringify(filtroUsuarioIds));
  }, [filtroUsuarioIds]);

  useEffect(() => {
    localStorage.setItem('master_feedback_filtro_tag_ids', JSON.stringify(filtroTagIds));
  }, [filtroTagIds]);

  // Delegation Actions
  async function delegarUsuario(status: string, usuarioId: string) {
    try {
      const res = await api.post('/feedback/colunas/delegar', { status, usuarioId });
      if (res.data.ok) {
        toast.success('Convite de responsabilidade enviado!');
        setColunaDelegando(null);
        setBuscaDelegar('');
        // Recarregar dados silenciosamente
        api.get('/feedback/admin').then((fbRes) => {
          setFeedbacks(fbRes.data.feedbacks || []);
          setResponsabilidades(fbRes.data.responsabilidades || []);
          setPresencas(fbRes.data.presencas || {});
        });
      }
    } catch {
      toast.error('Erro ao convidar usuário.');
    }
  }

  async function aceitarConvite(status: string) {
    try {
      const res = await api.post('/feedback/colunas/aceitar', { status });
      if (res.data.ok) {
        toast.success('Convite aceito!');
        api.get('/feedback/admin').then((fbRes) => {
          setFeedbacks(fbRes.data.feedbacks || []);
          setResponsabilidades(fbRes.data.responsabilidades || []);
          setPresencas(fbRes.data.presencas || {});
        });
      }
    } catch {
      toast.error('Erro ao aceitar convite.');
    }
  }

  async function recusarConvite(status: string, usuarioId?: string) {
    try {
      const res = await api.post('/feedback/colunas/recusar', { status, usuarioId });
      if (res.data.ok) {
        toast.success(usuarioId ? 'Responsável removido.' : 'Convite recusado.');
        api.get('/feedback/admin').then((fbRes) => {
          setFeedbacks(fbRes.data.feedbacks || []);
          setResponsabilidades(fbRes.data.responsabilidades || []);
          setPresencas(fbRes.data.presencas || {});
        });
      }
    } catch {
      toast.error('Erro ao processar remoção.');
    }
  }

  const getPresenceStatus = (userId: string): 'offline' | 'online' | 'ocioso' => {
    const p = presencas[userId];
    if (!p) return 'offline';
    return (p.status as 'offline' | 'online' | 'ocioso') || 'offline';
  };

  const getUsuariosDisponiveis = (status: string) => {
    const delegatedIds = responsabilidades
      .filter((r: any) => r.status === status)
      .map((r: any) => r.usuarioId);
    return usuarios.filter((u: any) => 
      (u.perfil === 'ADMIN' || u.perfil === 'MUNICIPAL' || u.perfil === 'ESTADUAL' || u.perfil === 'REDE') && 
      !delegatedIds.includes(u.id)
    );
  };



  // Handle Drop in Kanban
  async function handleDropKanban(itemId: string, targetStatus: string) {
    // Optimistic update
    setFeedbacks((prev: Feedback[]) => prev.map((f: Feedback) => f.id === itemId ? { ...f, status: targetStatus } : f));

    try {
      await api.patch(`/feedback/${itemId}`, { status: targetStatus });
      toast.success(`Status atualizado para ${targetStatus.replace('_', ' ')}`);
      
      // Carrega dados de forma silenciosa para evitar flash da tela de carregamento inteiro
      const fbRes = await api.get('/feedback/admin');
      setFeedbacks(fbRes.data.feedbacks || []);
      setResponsabilidades(fbRes.data.responsabilidades || []);
      setPresencas(fbRes.data.presencas || {});
    } catch (err: any) {
      toast.error('Erro ao atualizar status do feedback');
      carregarDados();
    }
  }

  // Column Sub Header rendering logic for KanbanBoard
  const renderColumnSubHeader = (col: any) => {
    return (
      <>
        {/* Float Custom Dropdown for delegation */}
        {colunaDelegando === col.id && (
          <div style={{
            position: 'absolute', top: 32, right: 0, width: 280,
            background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color)',
            borderRadius: 12, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            zIndex: 50, padding: 10, display: 'flex', flexDirection: 'column', gap: 8
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-main)' }}>Delegar Administrador</span>
              <button
                type="button" onClick={() => setColunaDelegando(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                <X size={12} />
              </button>
            </div>

            {/* Search in Dropdown */}
            <div style={{ position: 'relative' }}>
              <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text" placeholder="Pesquisar administrador..."
                value={buscaDelegar} onChange={(e) => setBuscaDelegar(e.target.value)}
                className="form-input"
                style={{ padding: '4px 8px 4px 26px', fontSize: '0.72rem', borderRadius: 6 }}
              />
            </div>

            {/* Available Users List */}
            <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(() => {
                const list = getUsuariosDisponiveis(col.id).filter((u: any) => 
                  u.nome.toLowerCase().includes(buscaDelegar.toLowerCase()) ||
                  u.email.toLowerCase().includes(buscaDelegar.toLowerCase())
                );

                if (list.length === 0) {
                  return (
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
                      Nenhum administrador disponível.
                    </div>
                  );
                }

                return list.map((user: any) => {
                  return (
                    <div
                      key={user.id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 6px', borderRadius: 8, border: '1px solid transparent',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-body, rgba(0,0,0,0.02))'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.58rem', color: '#fff', fontWeight: 800, flexShrink: 0
                        }}>
                          {getInitials(user.nome)}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.nome}
                          </span>
                          <span style={{ fontSize: '0.58rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                            {user.perfil}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => delegarUsuario(col.id, user.id)}
                        style={{
                          padding: '2px 8px', borderRadius: 6, border: 'none',
                          background: '#8b5cf6', color: 'white', fontSize: '0.64rem',
                          fontWeight: 700, cursor: 'pointer'
                        }}
                      >
                        Convidar
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Responsible Users Row */}
        {(() => {
          const colResps = responsabilidades.filter((r: any) => r.status === col.id);
          const sortedResps = [...colResps].sort((a: any, b: any) => {
            const statusA = getPresenceStatus(a.usuarioId);
            const statusB = getPresenceStatus(b.usuarioId);
            const val: Record<string, number> = { online: 0, ocioso: 1, offline: 2 };
            return val[statusA] - val[statusB];
          });

          if (sortedResps.length === 0) return null;

          return (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 2px',
              borderBottom: '1px solid var(--border-color)', paddingBottom: 8, marginBottom: 4
            }}>
              {sortedResps.map((resp: any) => {
                const presence = getPresenceStatus(resp.usuarioId);
                const isPending = !resp.aceito;
                const pColor = presence === 'online' ? '#10b981' : presence === 'ocioso' ? '#eab308' : '#94a3b8';
                const pTitle = presence === 'online' ? 'Online na Central' : presence === 'ocioso' ? 'Ocioso (em outra tela)' : 'Offline';

                return (
                  <div
                    key={resp.id}
                    title={`${resp.usuario?.nome} (${resp.usuario?.perfil}) - ${pTitle}${isPending ? ' (Pendente)' : ''}`}
                    style={{
                      position: 'relative', display: 'flex', alignItems: 'center', gap: 4,
                      background: 'var(--bg-body, rgba(0,0,0,0.02))', padding: '2px 6px',
                      borderRadius: 20, border: isPending ? '1px dashed #8b5cf6' : '1px solid var(--border-color)',
                      opacity: isPending ? 0.65 : 1
                    }}
                  >
                    <div style={{ position: 'relative', width: 16, height: 16 }}>
                      {resp.usuario?.fotoUrl ? (
                        <img
                          src={resp.usuario.fotoUrl}
                          alt={resp.usuario.nome}
                          style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: '100%', height: '100%', borderRadius: '50%',
                          background: 'linear-gradient(135deg, #64748b, #475569)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.45rem', color: '#fff', fontWeight: 800
                        }}>
                          {getInitials(resp.usuario?.nome || '?')}
                        </div>
                      )}
                      {/* Presence dot */}
                      <div style={{
                        position: 'absolute', bottom: -1, right: -1, width: 6, height: 6,
                        borderRadius: '50%', background: pColor, border: '1px solid var(--bg-card, #fff)'
                      }} />
                    </div>
                    <span style={{ fontSize: '0.64rem', fontWeight: 700, color: 'var(--text-main)' }}>
                      {resp.usuario?.nome ? resp.usuario.nome.split(' ')[0] : 'Admin'}
                    </span>
                    
                    {/* Hover/Click to remove delegacy */}
                    <button
                      type="button"
                      onClick={() => recusarConvite(col.id, resp.usuarioId)}
                      style={{
                        background: 'transparent', border: 'none', color: '#ef4444',
                        cursor: 'pointer', padding: 0, fontSize: '0.65rem', display: 'flex', alignItems: 'center'
                      }}
                      title="Remover responsabilidade"
                    >
                      <X size={10} />
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Pending Invitation Banner */}
        {(() => {
          const convitePendente = responsabilidades.find(
            (r: any) => r.status === col.id && r.usuarioId === currentUser?.id && !r.aceito
          );
          if (!convitePendente) return null;

          return (
            <div style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.12) 0%, rgba(59, 130, 246, 0.12) 100%)',
              border: '1.5px dashed #8b5cf6',
              borderRadius: 12,
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: 'center',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.05)',
              marginBottom: 4
            }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-main)' }}>
                Você foi convidado para ser responsável por esta coluna!
              </div>
              <div style={{ display: 'flex', gap: 6, width: '100%' }}>
                <button
                  onClick={() => aceitarConvite(col.id)}
                  style={{
                    flex: 1, padding: '5px 8px', borderRadius: 8, border: 'none',
                    background: '#10b981', color: 'white', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Aceitar
                </button>
                <button
                  onClick={() => recusarConvite(col.id)}
                  style={{
                    flex: 1, padding: '5px 8px', borderRadius: 8, border: 'none',
                    background: '#ef4444', color: 'white', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Recusar
                </button>
              </div>
            </div>
          );
        })()}
      </>
    );
  };

  // Column Header Right rendering logic for KanbanBoard
  const renderColumnHeaderRight = (col: any) => {
    return (
      <button
        type="button"
        onClick={() => {
          setColunaDelegando(colunaDelegando === col.id ? null : col.id);
          setBuscaDelegar('');
        }}
        style={{
          background: 'transparent', border: 'none', padding: 2, cursor: 'pointer',
          color: 'var(--text-muted)', display: 'flex', alignItems: 'center'
        }}
        title="Delegar administradores"
      >
        <UserPlus size={14} />
      </button>
    );
  };

  // Save Response
  async function enviarResposta(feedbackId: string) {
    if (!resposta.trim()) return;
    setRespondendo(true);
    try {
      const res = await api.patch(`/feedback/${feedbackId}`, {
        resposta,
        status: 'RESOLVIDO'
      });
      if (res.data.ok) {
        toast.success('Resposta enviada e feedback marcado como Resolvido!');
        setResposta('');
        setSelectedFeedback(res.data.feedback);
        carregarDados();
      }
    } catch {
      toast.error('Erro ao responder feedback');
    }
    setRespondendo(false);
  }

  // Generic Update
  async function handleUpdateField(feedbackId: string, payload: Record<string, any>) {
    try {
      const res = await api.patch(`/feedback/${feedbackId}`, payload);
      if (res.data.ok) {
        toast.success('Feedback atualizado com sucesso');
        setSelectedFeedback(res.data.feedback);
        carregarDados();
      }
    } catch {
      toast.error('Erro ao atualizar dados do feedback');
    }
  }

  // Tag association toggler
  async function handleToggleTag(feedback: Feedback, tagId: string) {
    const currentTagIds = feedback.tags?.map(t => t.tag.id) || [];
    let newTagIds: string[];
    if (currentTagIds.includes(tagId)) {
      newTagIds = currentTagIds.filter(id => id !== tagId);
    } else {
      newTagIds = [...currentTagIds, tagId];
    }
    await handleUpdateField(feedback.id, { tagIds: newTagIds });
  }

  // Helper to count how many feedbacks a user has in total
  const feedbackCountByUser = useMemo(() => {
    const counts: Record<string, number> = {};
    feedbacks.forEach(fb => {
      counts[fb.usuarioId] = (counts[fb.usuarioId] || 0) + 1;
    });
    return counts;
  }, [feedbacks]);

  // Smart Search parser
  const parsedSearch = useMemo(() => {
    const result = {
      text: '',
      prioridades: [] as string[],
      tipos: [] as string[],
      statuses: [] as string[],
      feedbacksGt: null as number | null,
      feedbacksLt: null as number | null,
    };

    if (!busca.trim()) return result;

    const tokens = busca.split(/\s+/);
    const textParts: string[] = [];

    tokens.forEach(token => {
      const lower = token.toLowerCase();
      if (lower.startsWith('prioridade:')) {
        const val = token.substring(11).toUpperCase();
        if (val) result.prioridades.push(val);
      } else if (lower.startsWith('tipo:')) {
        const val = token.substring(5).toUpperCase();
        if (val === 'PROBLEMA' || val === 'SUGESTAO' || val === 'DUVIDA') {
          result.tipos.push(val);
        } else if (val === 'BUG') {
          result.tipos.push('PROBLEMA');
        }
      } else if (lower.startsWith('status:')) {
        const val = token.substring(7).toUpperCase();
        if (val) result.statuses.push(val);
      } else if (lower.startsWith('feedbacks:>')) {
        const val = parseInt(token.substring(11), 10);
        if (!isNaN(val)) result.feedbacksGt = val;
      } else if (lower.startsWith('feedbacks:<')) {
        const val = parseInt(token.substring(11), 10);
        if (!isNaN(val)) result.feedbacksLt = val;
      } else if (lower.startsWith('qtd:>')) {
        const val = parseInt(token.substring(5), 10);
        if (!isNaN(val)) result.feedbacksGt = val;
      } else if (lower.startsWith('qtd:<')) {
        const val = parseInt(token.substring(5), 10);
        if (!isNaN(val)) result.feedbacksLt = val;
      } else {
        textParts.push(token);
      }
    });

    result.text = textParts.join(' ').trim();
    return result;
  }, [busca]);

  // Client filtering
  const filtrados = useMemo(() => {
    return feedbacks.filter((fb) => {
      // Perfil filter
      if (filtroPerfil !== 'TODOS' && fb.usuario?.perfil !== filtroPerfil) return false;

      // Resolve hierarchy dynamically
      const resolvedEscolaId = fb.escolaId || fb.usuario?.escolaId;
      let resolvedMunicipioId = fb.usuario?.municipioId || null;
      let resolvedEstadoId = fb.usuario?.estadoId || null;
      let resolvedRedeId = fb.usuario?.redeId || null;

      if (resolvedEscolaId) {
        const esc = escolas.find((x: any) => x.id === resolvedEscolaId);
        if (esc) {
          resolvedMunicipioId = esc.municipioId;
        }
      }
      if (resolvedMunicipioId) {
        const mun = municipios.find((x: any) => x.id === resolvedMunicipioId);
        if (mun) {
          resolvedEstadoId = mun.estadoId;
        }
      }
      if (resolvedEstadoId) {
        const est = estados.find((x: any) => x.id === resolvedEstadoId);
        if (est) {
          resolvedRedeId = est.redeId;
        }
      }

      // Rede filter
      if (filtroRedes.length > 0 && (!resolvedRedeId || !filtroRedes.includes(resolvedRedeId))) return false;

      // Estado filter
      if (filtroEstados.length > 0 && (!resolvedEstadoId || !filtroEstados.includes(resolvedEstadoId))) return false;

      // Municipio filter
      if (filtroMunicipios.length > 0 && (!resolvedMunicipioId || !filtroMunicipios.includes(resolvedMunicipioId))) return false;

      // Escola filter
      if (filtroEscolas.length > 0 && (!resolvedEscolaId || !filtroEscolas.includes(resolvedEscolaId))) return false;

      // Statuses multi-select filter
      if (filtroStatuses.length > 0 && !filtroStatuses.includes(fb.status)) return false;
      if (filtroTipo !== 'TODOS' && fb.tipo !== filtroTipo) return false;
      if (filtroPrioridade !== 'TODAS' && fb.prioridade !== filtroPrioridade) return false;

      // User responsible / creator filter (multi-select)
      if (filtroUsuarioIds.length > 0) {
        const matchesUser =
          filtroUsuarioIds.includes(fb.usuarioId) ||
          (fb.triadoPorId && filtroUsuarioIds.includes(fb.triadoPorId)) ||
          (fb.analisadoPorId && filtroUsuarioIds.includes(fb.analisadoPorId));
        if (!matchesUser) return false;
      }

      // Tag filter (multi-select)
      if (filtroTagIds.length > 0) {
        const hasTag = fb.tags?.some(t => filtroTagIds.includes(t.tag.id));
        if (!hasTag) return false;
      }

      // Smart search filter expression matching
      const { text, prioridades, tipos, statuses, feedbacksGt, feedbacksLt } = parsedSearch;
      if (prioridades.length > 0 && !prioridades.includes(fb.prioridade)) return false;
      if (tipos.length > 0 && !tipos.includes(fb.tipo)) return false;
      if (statuses.length > 0 && !statuses.includes(fb.status)) return false;

      const userFbCount = feedbackCountByUser[fb.usuarioId] || 0;
      if (feedbacksGt !== null && userFbCount <= feedbacksGt) return false;
      if (feedbacksLt !== null && userFbCount >= feedbacksLt) return false;

      if (text) {
        const t = text.toLowerCase();
        const matchesSearch =
          fb.titulo.toLowerCase().includes(t) ||
          fb.descricao.toLowerCase().includes(t) ||
          fb.usuario?.nome.toLowerCase().includes(t);
        if (!matchesSearch) return false;
      }

      return true;
    });
  }, [feedbacks, filtroStatuses, filtroTipo, filtroPrioridade, filtroUsuarioIds, filtroTagIds, currentUser, filtroPerfil, filtroRedes, filtroEstados, filtroMunicipios, filtroEscolas, parsedSearch, feedbackCountByUser, escolas, municipios, estados]);

  // Statistics calculation for Hero
  const stats = useMemo(() => {
    const total = feedbacks.length;
    let abertos = 0;
    let resolvida = 0;
    let pendente = 0;
    let triagem = 0;
    let analise = 0;
    let correcao = 0;
    let teste = 0;
    let fechados = 0;

    let asReporter = 0;
    let asTriager = 0;
    let asAnalyzer = 0;

    feedbacks.forEach((fb) => {
      // Count statuses
      if (fb.status === 'ABERTO') abertos++;
      else if (fb.status === 'TRIAGEM') triagem++;
      else if (fb.status === 'EM_ANALISE') analise++;
      else if (fb.status === 'PENDENTE') pendente++;
      else if (fb.status === 'EM_CORRECAO') correcao++;
      else if (fb.status === 'EM_TESTE') teste++;
      else if (fb.status === 'RESOLVIDO') resolvida++;
      else if (fb.status === 'FECHADO') fechados++;

      // Count responsibilities for currentUser
      if (currentUser) {
        if (fb.usuarioId === currentUser.id) asReporter++;
        if (fb.triadoPorId === currentUser.id) asTriager++;
        if (fb.analisadoPorId === currentUser.id) asAnalyzer++;
      }
    });

    return {
      total,
      abertos,
      triagem,
      analise,
      pendente,
      correcao,
      teste,
      resolvida,
      fechados,
      asReporter,
      asTriager,
      asAnalyzer,
    };
  }, [feedbacks, currentUser]);

  // Grouped feedbacks by status for Kanban Column rendering
  const kanbanData = useMemo(() => {
    const groups: Record<string, Feedback[]> = {
      ABERTO: [],
      TRIAGEM: [],
      EM_ANALISE: [],
      PENDENTE: [],
      EM_CORRECAO: [],
      EM_TESTE: [],
      RESOLVIDO: [],
      FECHADO: [],
    };
    filtrados.forEach((fb) => {
      if (groups[fb.status]) {
        groups[fb.status].push(fb);
      }
    });
    return groups;
  }, [filtrados]);

  // Helpers for user initials
  function getInitials(name: string) {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }

  const statusOptions = useMemo(() => {
    return COLUMNS.map(col => ({
      id: col.id,
      nome: col.label,
      cor: col.color,
      subtitulo: 'Status'
    }));
  }, []);

  const userOptions: ACMultiSelectOption[] = useMemo(() => {
    return usuarios.map((u: any) => ({
      id: u.id,
      nome: u.nome,
      subtitulo: `${u.perfil} • ${u.email || ''}`,
      presence: getPresenceStatus(u.id),
      perfil: u.perfil
    }));
  }, [usuarios, presencas]);

  const prioridadeOptions = useMemo(() => [
    { id: 'BAIXA', nome: 'Baixa', cor: PRIORIDADE_COLORS.BAIXA.text },
    { id: 'MEDIA', nome: 'Média', cor: PRIORIDADE_COLORS.MEDIA.text },
    { id: 'ALTA', nome: 'Alta', cor: PRIORIDADE_COLORS.ALTA.text },
    { id: 'CRITICA', nome: 'Crítica', cor: PRIORIDADE_COLORS.CRITICA.text },
  ], []);

  const tipoOptions = useMemo(() => [
    { id: 'PROBLEMA', nome: 'Problema', cor: '#ef4444' },
    { id: 'SUGESTAO', nome: 'Sugestão', cor: '#eab308' },
    { id: 'DUVIDA', nome: 'Dúvida', cor: '#3b82f6' },
  ], []);

  const userOptionsWithNone: ACMultiSelectOption[] = useMemo(() => [
    { id: '__NONE__', nome: '— Não atribuído —', cor: '#94a3b8' },
    ...userOptions,
  ], [userOptions]);

  const navStripCounts = useMemo(() => {
    return COLUMNS.map(col => filtrados.filter(item => item.status === col.id).length);
  }, [filtrados]);

  const navStripVolumeInfo = useMemo(() => {
    const counts = COLUMNS.map(col => filtrados.filter(item => item.status === col.id).length);
    const nonZero = counts.filter(c => c > 0);
    const avg = nonZero.length ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 1;
    const stdDev = nonZero.length > 1
      ? Math.sqrt(nonZero.reduce((sum, c) => sum + (c - avg) ** 2, 0) / nonZero.length)
      : avg * 0.3;
    const safeStd = stdDev || avg * 0.2 || 1;

    return counts.map(count => {
      if (count === 0) return { bg: '#cbd5e1', shadow: 'rgba(148,163,184,.3)', label: 'Vazio' };
      const z = (count - avg) / safeStd;
      if (z < -1.2) return { bg: '#e2e8f0', shadow: 'rgba(148,163,184,.3)', label: 'Volume muito baixo' };
      if (z < -0.4) return { bg: '#3b82f6', shadow: 'rgba(59,130,246,.4)', label: 'Volume baixo' };
      if (z < 0.4)  return { bg: '#10b981', shadow: 'rgba(16,185,129,.4)', label: 'Volume normal' };
      if (z < 1.0)  return { bg: '#f59e0b', shadow: 'rgba(245,158,11,.4)', label: 'Volume acima da média' };
      if (z < 1.8)  return { bg: '#ef4444', shadow: 'rgba(239,68,68,.4)', label: 'Sobrecarga' };
      return { bg: '#1e293b', shadow: 'rgba(0,0,0,.5)', label: 'Crítico' };
    });
  }, [filtrados]);


  const hasActiveFilters = useMemo(() => {
    return (
      busca !== '' ||
      filtroStatuses.length > 0 ||
      filtroTipo !== 'TODOS' ||
      filtroPrioridade !== 'TODAS' ||
      filtroUsuarioIds.length > 0 ||
      filtroTagIds.length > 0 ||
      filtroPerfil !== 'ADMIN' ||
      filtroRedes.length > 0 ||
      filtroEstados.length > 0 ||
      filtroMunicipios.length > 0 ||
      filtroEscolas.length > 0
    );
  }, [
    busca, filtroStatuses, filtroTipo, filtroPrioridade, filtroUsuarioIds,
    filtroTagIds, filtroPerfil, filtroRedes, filtroEstados, filtroMunicipios, filtroEscolas
  ]);

if (loading) {
    return (
      <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={36} style={{ color: '#8b5cf6', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: 'var(--text-muted)', marginTop: 12, fontSize: '0.85rem' }}>Carregando dados da Central...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page animate-fadeIn" style={{ padding: '0', background: '#fff', minHeight: 'calc(100vh - 64px)' }}>
      
      {/* ── Trello Connection & Webhook Header Banner ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
        padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.08)', gap: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Avatar da conta Trello */}
          <div style={{ position: 'relative' }}>
            <img
              src={trelloAccount.avatarUrl}
              alt={trelloAccount.nome}
              style={{ width: 34, height: 34, borderRadius: '50%', border: '2px solid #818cf8' }}
            />
            <span style={{
              position: 'absolute', bottom: -2, right: -2, width: 10, height: 10, borderRadius: '50%',
              background: '#10b981', border: '2px solid #0f172a'
            }} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#ffffff' }}>
                {trelloAccount.nome}
              </span>
              <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#c7d2fe' }}>
                ({trelloAccount.username})
              </span>
              <span style={{
                fontSize: '0.58rem', fontWeight: 800, padding: '2px 8px', borderRadius: 4,
                background: 'rgba(16, 185, 129, 0.15)', color: '#34d399', border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                🟢 TRELLO CONECTADO
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '0.68rem', color: '#94a3b8' }}>
              Domínio: <span style={{ color: '#a5b4fc', fontWeight: 600 }}>alunoconectado.com.br</span> • Ambiente: {trelloAccount.ambiente}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => setModalServerIntegrationOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
              background: '#2563eb', color: '#ffffff', border: 'none',
              fontSize: '0.76rem', fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
            }}
          >
            <Settings size={14} style={{ color: '#ffffff' }} /> Integração Trello
          </button>

          <button
            type="button"
            onClick={() => setModalUserSettingsOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
              background: 'rgba(255, 255, 255, 0.08)', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.15)',
              fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer'
            }}
          >
            <User size={14} /> Configurações do Usuário
          </button>
        </div>
      </div>

      {/* ── Premium Filters Toolbar ── */}
      <div className="premium-filters-toolbar animate-fadeIn" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'stretch',
        margin: '0',
        border: 'none',
        borderRadius: '0',
        boxShadow: 'none',
        background: '#fff',
        padding: '16px 24px 8px 24px'
      }}>
        {/* Row 1: Search + Perfil + Tags + Priority + Type + Reset */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {/* Search */}
          <div className="dt-search" style={{ maxWidth: 220 }}>
            <Search size={14} className="dt-search-icon" />
            <input
              type="text"
              placeholder="Buscar título, descrição..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="dt-search-input"
              style={{ height: 38 }}
            />
          </div>

          {/* Perfil */}
          <div style={{ minWidth: 160 }}>
            <ACMultiSelectDropdown
              options={PERFIL_OPTIONS}
              selectedIds={filtroPerfil !== 'TODOS' ? [filtroPerfil] : []}
              onChange={(ids) => {
                const next = ids.length > 0 ? ids[0] : 'TODOS';
                setFiltroPerfil(next);
                if (next === 'ADMIN') {
                  setFiltroRedes([]);
                  setFiltroEstados([]);
                  setFiltroMunicipios([]);
                  setFiltroEscolas([]);
                  setFiltroUsuarioIds([]);
                }
              }}
              placeholder="Todos Perfis"
              searchPlaceholder="Pesquisar perfil..."
              showAvatars={false}
              singleSelect
            />
          </div>

          {/* Tags */}
          <div style={{ width: 160 }}>
            <ACTagsDropdownWithCRUD
              tags={tags}
              selectedTagIds={filtroTagIds}
              onChange={setFiltroTagIds}
              onCreateTag={async (nome, cor, grupo) => {
                try {
                  const res = await api.post('/feedback/tags', { nome, cor, grupo: grupo || null });
                  if (res.data.ok) {
                    toast.success('Tag criada com sucesso!');
                    const tagsRes = await api.get('/feedback/tags');
                    setTags(tagsRes.data.tags || []);
                  }
                } catch {
                  toast.error('Erro ao criar tag');
                }
              }}
              onDeleteTag={async (id) => {
                try {
                  const res = await api.delete(`/feedback/tags/${id}`);
                  if (res.data.ok) {
                    toast.success('Tag excluída com sucesso!');
                    const tagsRes = await api.get('/feedback/tags');
                    setTags(tagsRes.data.tags || []);
                    setFiltroTagIds(prev => prev.filter(x => x !== id));
                  }
                } catch {
                  toast.error('Erro ao excluir tag');
                }
              }}
            />
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--border-color, rgba(0,0,0,0.1))', alignSelf: 'center' }} />

          {/* Priority pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {prioridadeOptions.map((opt: any) => {
              const isActive = filtroPrioridade === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFiltroPrioridade(isActive ? 'TODAS' : opt.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 20,
                    border: `1px solid ${isActive ? opt.cor : 'var(--border-color, #cbd5e1)'}`,
                    background: isActive ? `${opt.cor}15` : 'transparent',
                    color: isActive ? opt.cor : 'var(--text-muted, #64748b)',
                    fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.cor, flexShrink: 0 }} />
                  {opt.nome}
                </button>
              );
            })}
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--border-color, rgba(0,0,0,0.1))', alignSelf: 'center' }} />

          {/* Type pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {tipoOptions.map((opt: any) => {
              const isActive = filtroTipo === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFiltroTipo(isActive ? 'TODOS' : opt.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 20,
                    border: `1px solid ${isActive ? opt.cor : 'var(--border-color, #cbd5e1)'}`,
                    background: isActive ? `${opt.cor}15` : 'transparent',
                    color: isActive ? opt.cor : 'var(--text-muted, #64748b)',
                    fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.cor, flexShrink: 0 }} />
                  {opt.nome}
                </button>
              );
            })}
          </div>

          {/* Reset */}
          {hasActiveFilters && (
            <button
              onClick={() => {
                setBusca('');
                setFiltroStatuses([]);
                setFiltroTipo('TODOS');
                setFiltroPrioridade('TODAS');
                setFiltroUsuarioIds([]);
                setFiltroTagIds([]);
                setFiltroPerfil('ADMIN');
                setFiltroRedes([]);
                setFiltroEstados([]);
                setFiltroMunicipios([]);
                setFiltroEscolas([]);
              }}
              style={{
                border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                padding: '6px 12px', borderRadius: 8, height: 32, marginLeft: 'auto'
              }}
            >
              <RefreshCw size={10} /> Limpar
            </button>
          )}
        </div>

        {/* Row 2: Hierarchy (Redes + Estados + Municípios + Escolas) + Users */}
        {filtroPerfil !== 'ADMIN' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))', paddingTop: 10 }}>
            {/* Hierarchy Selection */}
            <ACHierarchySelector
              redes={redes}
              estados={estados}
              municipios={municipios}
              escolas={escolas}
              selectedRedes={filtroRedes}
              onChangeRedes={setFiltroRedes}
              selectedEstados={filtroEstados}
              onChangeEstados={setFiltroEstados}
              selectedMunicipios={filtroMunicipios}
              onChangeMunicipios={setFiltroMunicipios}
              selectedEscolas={filtroEscolas}
              onChangeEscolas={setFiltroEscolas}
            />

            {/* Users */}
            <div style={{ width: 150 }}>
              <ACMultiSelectDropdown
                options={userOptions}
                selectedIds={filtroUsuarioIds}
                onChange={setFiltroUsuarioIds}
                placeholder="Todos Usuários"
                searchPlaceholder="Pesquisar usuário..."
                showAvatars={true}
                showPresence={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Kanban Board ── */}
      <div style={{ padding: '0 24px 24px 24px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <KanbanBoard<Feedback>
          style={{
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            background: '#fff',
            padding: '0'
          }}
          columns={COLUMNS}
          items={filtrados}
          getColumnId={(fb) => fb.status}
          onDrop={handleDropKanban}
          onCardClick={(fb) => setSelectedFeedback(fb)}
          disableCollapse={true}
          renderColumnHeaderRight={renderColumnHeaderRight}
          renderColumnSubHeader={renderColumnSubHeader}
          isLoading={loading}
          loadingText="Carregando feedbacks..."
          hideNav={true}
          renderCard={(fb) => {
            const pri = PRIORIDADE_COLORS[fb.prioridade] || PRIORIDADE_COLORS.MEDIA;
            const isVideo = fb.screenshot?.endsWith('.webm') || fb.screenshot?.endsWith('.mp4') || fb.screenshot?.includes('/serve-local/');
            
            return (
              <>
                {/* Title */}
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-main)', lineHeight: 1.3 }}>
                  {fb.titulo}
                </div>

                {/* Dynamic tags list */}
                {fb.tags && fb.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {fb.tags.map((t) => (
                      <span
                        key={t.tag.id}
                        style={{
                          fontSize: '0.58rem', fontWeight: 800, padding: '1px 6px', borderRadius: 4,
                          background: `${t.tag.cor}15`, color: t.tag.cor, border: `1px solid ${t.tag.cor}25`
                        }}
                      >
                        {t.tag.nome}
                      </span>
                    ))}
                  </div>
                )}

                {/* Footer details row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                  {/* Icons details */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {/* Media Indicator */}
                    {fb.screenshot && (
                      <span title={isVideo ? 'Vídeo gravado' : 'Screenshot capturada'} style={{ display: 'flex', alignItems: 'center', color: '#8b5cf6' }}>
                        {isVideo ? <PlayCircle size={12} /> : <Eye size={12} />}
                      </span>
                    )}
                    {/* Priority pill */}
                    <span style={{
                      fontSize: '0.58rem', fontWeight: 800, padding: '1px 5px', borderRadius: 4,
                      background: pri.bg, color: pri.text, border: `1px solid ${pri.border}`
                    }}>
                      {fb.prioridade}
                    </span>
                  </div>

                  {/* Assignee / Creator avatars stack */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: -4 }}>
                    {/* Creator avatar */}
                    <div
                      title={`Reportado por: ${fb.usuario?.nome}`}
                      style={{
                        width: 18, height: 18, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.52rem', color: '#fff', fontWeight: 800,
                        border: '1.5px solid var(--bg-card, #fff)'
                      }}
                    >
                      {fb.usuario?.fotoUrl ? (
                        <img src={fb.usuario.fotoUrl} alt={fb.usuario.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        getInitials(fb.usuario?.nome || '?')
                      )}
                    </div>

                    {/* Triager avatar */}
                    {fb.triadoPor && (
                      <div
                        title={`Triado por: ${fb.triadoPor.nome}`}
                        style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.52rem', color: '#fff', fontWeight: 800,
                          border: '1.5px solid var(--bg-card, #fff)',
                          marginLeft: -4
                        }}
                      >
                        {fb.triadoPor.fotoUrl ? (
                          <img src={fb.triadoPor.fotoUrl} alt={fb.triadoPor.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          getInitials(fb.triadoPor.nome)
                        )}
                      </div>
                    )}

                    {/* Analyzer avatar */}
                    {fb.analisadoPor && (
                      <div
                        title={`Analisado por: ${fb.analisadoPor.nome}`}
                        style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.52rem', color: '#fff', fontWeight: 800,
                          border: '1.5px solid var(--bg-card, #fff)',
                          marginLeft: -4
                        }}
                      >
                        {fb.analisadoPor.fotoUrl ? (
                          <img src={fb.analisadoPor.fotoUrl} alt={fb.analisadoPor.nome} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          getInitials(fb.analisadoPor.nome)
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          }}
        />
      </div>


      {/* ── Modal: Details & Governance (Trello Split Style) ── */}
      {selectedFeedback && (
        <Modal
          open={!!selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          title=""
          size="lg"
          contentClassName="umu-wizard-modal"
        >
          {/* Banner — fixed at top */}
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: '12px 12px 0 0', padding: '16px 24px',
            position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.08)',
            flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.64rem', fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#7c3aed', color: '#fff' }}>
                  {TIPO_LABELS[selectedFeedback.tipo] || selectedFeedback.tipo}
                </span>
                <h2 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
                  {selectedFeedback.titulo}
                </h2>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.72rem', margin: '4px 0 0' }}>
                ID: #{selectedFeedback.id.substring(0, 8)} • Rota: <span style={{ color: '#a78bfa', fontWeight: 600 }}>{selectedFeedback.rota}</span>
              </p>
            </div>

            {selectedFeedback.usuario?.id && (
              <button
                type="button"
                onClick={() => {
                  setSelectedFeedback(null);
                  navigate(`/master/chat?contatoId=${selectedFeedback.usuario?.id}`);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                  background: '#7c3aed', color: '#fff', border: 'none', fontSize: '0.74rem',
                  fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(124, 58, 237, 0.2)'
                }}
              >
                <MessageSquare size={14} /> Abrir Chat de Suporte
              </button>
            )}
          </div>

          {/* 2-Column Split Content Layout (Trello Style) */}
          <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            
            {/* 👈 LEFT PANEL: Details, Media Gallery, Governance & Tags (Width 60%) */}
            <div style={{
              flex: '1 1 60%', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18,
              overflowY: 'auto', borderRight: '1px solid var(--border-color, #e2e8f0)'
            }}>
              
              {/* Formatted Description & IA Analysis */}
              <div>
                <h4 style={{ fontSize: '0.76rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                  📝 Descrição & Análise de IA
                </h4>
                {formatDescricao(selectedFeedback.descricao)}
              </div>

              {/* Main Screenshot/Video */}
              {selectedFeedback.screenshot && (
                <div style={{
                  background: 'var(--bg-card, #f8fafc)', padding: 12, borderRadius: 12,
                  border: '1px solid var(--border-color)'
                }}>
                  {(() => {
                    const isVideo = selectedFeedback.screenshot?.endsWith('.webm') || selectedFeedback.screenshot?.endsWith('.mp4') || selectedFeedback.screenshot?.includes('/serve-local/');
                    let resolvedUrl = selectedFeedback.screenshot || '';
                    if (resolvedUrl) {
                      resolvedUrl = resolvedUrl.replace(/^https?:\/\/[^\/]+(?=\/(api\/v1|ecad))/, '');
                      if (!resolvedUrl.startsWith('http') && !resolvedUrl.startsWith('blob:')) {
                        let path = resolvedUrl;
                        if (path.startsWith('/api/v1/')) {
                          resolvedUrl = path;
                        } else {
                          if (path.startsWith('/ecad/')) path = path.substring(5);
                          else if (path.startsWith('ecad/')) path = path.substring(4);
                          resolvedUrl = `/api/v1/ecad${path.startsWith('/') ? '' : '/'}${path}`;
                        }
                      }
                    }

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <span style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {isVideo ? '🎥 Gravação de Tela anexada' : '🖼️ Captura de Tela anexada'}
                        </span>
                        <div
                          onClick={() => {
                            if (isVideo) setVideoModalUrl(resolvedUrl);
                            else setImageModalUrl(resolvedUrl);
                          }}
                          style={{
                            position: 'relative', width: '100%', height: 160, borderRadius: 10, overflow: 'hidden',
                            cursor: 'pointer', background: '#090d16', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '1px solid var(--border-color)'
                          }}
                        >
                          {isVideo ? (
                            <video src={resolvedUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                          ) : (
                            <img src={resolvedUrl} alt="Anexo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                          <div style={{
                            position: 'absolute', width: 40, height: 40, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                          }}>
                            {isVideo ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                            ) : (
                              <Eye size={18} style={{ color: 'white' }} />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* 📎 Multiple Attachments Gallery (Trello Parity) */}
              <div style={{
                background: 'var(--bg-card, #f8fafc)', padding: 14, borderRadius: 12,
                border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 10
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    📎 Galeria de Anexos ({selectedFeedback.anexos?.length || 0})
                  </span>
                </div>

                {/* List of attachments */}
                {selectedFeedback.anexos && selectedFeedback.anexos.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {selectedFeedback.anexos.map((anexo) => (
                      <div
                        key={anexo.id}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid var(--border-color, #e2e8f0)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span style={{ fontSize: '1rem' }}>
                            {anexo.tipo === 'VIDEO' ? '🎥' : anexo.tipo === 'IMAGE' ? '🖼️' : '📄'}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <a
                              href={anexo.url} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: '0.76rem', fontWeight: 700, color: '#7c3aed', textDecoration: 'none', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                            >
                              {anexo.nome}
                            </a>
                            <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                              Enviado por {anexo.enviadoPor?.nome || 'Usuário'} • {new Date(anexo.criadoEm).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <a
                          href={anexo.url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6', background: 'rgba(59,130,246,0.1)', padding: '4px 10px', borderRadius: 6, textDecoration: 'none' }}
                        >
                          Abrir
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0 }}>Nenhum anexo adicional enviado.</p>
                )}

                {/* Add new attachment input */}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <input
                    type="text" placeholder="Nome do arquivo..."
                    value={novoAnexoNome} onChange={(e) => setNovoAnexoNome(e.target.value)}
                    className="form-input" style={{ fontSize: '0.74rem', flex: 1 }}
                  />
                  <input
                    type="text" placeholder="URL do arquivo (https://...)"
                    value={novoAnexoUrl} onChange={(e) => setNovoAnexoUrl(e.target.value)}
                    className="form-input" style={{ fontSize: '0.74rem', flex: 2 }}
                  />
                  <button
                    type="button"
                    onClick={() => enviarNovoAnexo(selectedFeedback.id)}
                    disabled={enviandoAnexo || !novoAnexoNome.trim() || !novoAnexoUrl.trim()}
                    style={{
                      padding: '0 14px', borderRadius: 8, border: 'none', background: '#7c3aed',
                      color: 'white', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer',
                      opacity: enviandoAnexo || !novoAnexoNome.trim() || !novoAnexoUrl.trim() ? 0.5 : 1
                    }}
                  >
                    + Anexar
                  </button>
                </div>
              </div>

              {/* 🛡️ Governance Panel (3-Level Governance) */}
              <div style={{
                background: 'var(--bg-card, #f8fafc)', padding: 14, borderRadius: 12,
                border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12
              }}>
                {/* Level 1: Reporter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.64rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    1. Abertura (Autor)
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', background: '#10b98120', color: '#10b981',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.66rem', fontWeight: 700
                    }}>
                      {getInitials(selectedFeedback.usuario?.nome || 'Usuário')}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-main)' }}>
                        {selectedFeedback.usuario?.nome}
                      </div>
                      <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                        Perfil: {selectedFeedback.usuario?.perfil}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Level 2: Triager */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.64rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    2. Triagem (Responsável)
                  </span>
                  <div style={{ marginTop: 2 }}>
                    <ACMultiSelectDropdown
                      options={userOptionsWithNone}
                      selectedIds={selectedFeedback.triadoPorId ? [selectedFeedback.triadoPorId] : ['__NONE__']}
                      onChange={(ids) => {
                        const val = ids[0] === '__NONE__' || !ids[0] ? null : ids[0];
                        handleUpdateField(selectedFeedback.id, { triadoPorId: val });
                      }}
                      placeholder="Selecionar triador..."
                      searchPlaceholder="Pesquisar..."
                      showAvatars={true}
                      showPresence={true}
                      singleSelect
                    />
                  </div>
                </div>

                {/* Level 3: Analyzer */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.64rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    3. Analisado Por (Auditoria)
                  </span>
                  <div style={{ marginTop: 2 }}>
                    <ACMultiSelectDropdown
                      options={userOptionsWithNone}
                      selectedIds={selectedFeedback.analisadoPorId ? [selectedFeedback.analisadoPorId] : ['__NONE__']}
                      onChange={(ids) => {
                        const val = ids[0] === '__NONE__' || !ids[0] ? null : ids[0];
                        handleUpdateField(selectedFeedback.id, { analisadoPorId: val });
                      }}
                      placeholder="Selecionar analista..."
                      searchPlaceholder="Pesquisar..."
                      showAvatars={true}
                      showPresence={true}
                      singleSelect
                    />
                  </div>
                </div>
              </div>

              {/* Workflow Control Box & Tags */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 }}>
                  <label className="form-label">Estágio do Fluxo (Status)</label>
                  <ACMultiSelectDropdown
                    options={statusOptions}
                    selectedIds={[selectedFeedback.status]}
                    onChange={(ids) => {
                      if (ids.length > 0) handleUpdateField(selectedFeedback.id, { status: ids[0] });
                    }}
                    placeholder="Selecionar status..."
                    showAvatars={false}
                    singleSelect
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 }}>
                  <label className="form-label">Prioridade</label>
                  <ACMultiSelectDropdown
                    options={prioridadeOptions}
                    selectedIds={[selectedFeedback.prioridade]}
                    onChange={(ids) => {
                      if (ids.length > 0) handleUpdateField(selectedFeedback.id, { prioridade: ids[0] });
                    }}
                    placeholder="Selecionar prioridade..."
                    showAvatars={false}
                    singleSelect
                  />
                </div>
              </div>

            </div>

            {/* 👉 RIGHT PANEL: Infinite Comments Stream & Activity History (Width 40%) */}
            <div style={{
              flex: '1 1 40%', padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 14,
              background: '#f8fafc', overflowY: 'auto'
            }}>
              
              {/* Tab Selector: Comentários vs. Histórico */}
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e2e8f0)', paddingBottom: 6 }}>
                <button
                  type="button"
                  onClick={() => setActiveTabRight('comentarios')}
                  style={{
                    flex: 1, padding: '6px 0', border: 'none', background: 'transparent',
                    borderBottom: activeTabRight === 'comentarios' ? '2px solid #7c3aed' : '2px solid transparent',
                    color: activeTabRight === 'comentarios' ? '#7c3aed' : 'var(--text-muted)',
                    fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                  }}
                >
                  💬 Comentários ({selectedFeedback.comentarios?.length || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTabRight('historico')}
                  style={{
                    flex: 1, padding: '6px 0', border: 'none', background: 'transparent',
                    borderBottom: activeTabRight === 'historico' ? '2px solid #7c3aed' : '2px solid transparent',
                    color: activeTabRight === 'historico' ? '#7c3aed' : 'var(--text-muted)',
                    fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                  }}
                >
                  📜 Histórico ({selectedFeedback.historico?.length || 0})
                </button>
              </div>

              {/* TAB 1: Stream de Comentários Infinitos */}
              {activeTabRight === 'comentarios' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
                  
                  {/* List of comments */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, overflowY: 'auto', maxHeight: 340, paddingRight: 4 }}>
                    {selectedFeedback.comentarios && selectedFeedback.comentarios.length > 0 ? (
                      selectedFeedback.comentarios.map((c) => (
                        <div
                          key={c.id}
                          style={{
                            background: '#fff', padding: '10px 12px', borderRadius: 10,
                            border: '1px solid var(--border-color, #e2e8f0)', boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{
                                width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', color: '#fff', fontWeight: 800
                              }}>
                                {getInitials(c.autor?.nome || 'Usuário')}
                              </div>
                              <span style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-main)' }}>
                                {c.autor?.nome || 'Usuário'}
                              </span>
                              <span style={{ fontSize: '0.58rem', fontWeight: 800, padding: '1px 5px', borderRadius: 4, background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed' }}>
                                {c.autor?.perfil || 'Membro'}
                              </span>
                            </div>
                            <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                              {new Date(c.criadoEm).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p style={{ fontSize: '0.78rem', color: 'var(--text-main)', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                            {c.conteudo}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.76rem' }}>
                        Nenhum comentário enviado ainda. Seja o primeiro a comentar!
                      </div>
                    )}
                  </div>

                  {/* Input box for new comment */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 'auto', background: '#fff', padding: 10, borderRadius: 10, border: '1px solid var(--border-color, #e2e8f0)' }}>
                    <textarea
                      rows={2}
                      placeholder="Escreva um comentário contínuo..."
                      value={novoComentario}
                      onChange={(e) => setNovoComentario(e.target.value)}
                      className="form-input"
                      style={{ fontSize: '0.76rem', resize: 'none', padding: 8 }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => enviarNovoComentario(selectedFeedback.id)}
                        disabled={enviandoComentario || !novoComentario.trim()}
                        style={{
                          padding: '6px 16px', borderRadius: 8, border: 'none', background: '#7c3aed',
                          color: 'white', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer',
                          opacity: enviandoComentario || !novoComentario.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 4
                        }}
                      >
                        <Send size={12} /> Comentar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: Feed de Atividades Cronológico */}
              {activeTabRight === 'historico' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto', maxHeight: 420 }}>
                  {selectedFeedback.historico && selectedFeedback.historico.length > 0 ? (
                    selectedFeedback.historico.map((h) => (
                      <div
                        key={h.id}
                        style={{
                          fontSize: '0.72rem', color: 'var(--text-main)', padding: '6px 10px',
                          borderLeft: '2px solid #7c3aed', background: '#fff', borderRadius: '0 6px 6px 0'
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{h.usuario?.nome || 'Sistema'}</span> {h.descricao}
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {new Date(h.criadoEm).toLocaleString()}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.76rem' }}>
                      Nenhuma alteração de histórico registrada.
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </Modal>
      )}

      {/* Video Modal Player */}
      {videoModalUrl && (
        <Modal
          open={!!videoModalUrl}
          onClose={() => setVideoModalUrl(null)}
          title="Visualizar Gravação de Tela"
          size="lg"
        >
          <div style={{ display: 'flex', justifyContent: 'center', background: '#090d16', borderRadius: 12, overflow: 'hidden' }}>
            <video 
              src={videoModalUrl} 
              controls 
              autoPlay
              style={{ width: '100%', maxHeight: '70vh', display: 'block' }}
            />
          </div>
        </Modal>
      )}

      {/* Image Modal Player */}
      {imageModalUrl && (
        <Modal
          open={!!imageModalUrl}
          onClose={() => setImageModalUrl(null)}
          title="Visualizar Captura de Tela"
          size="lg"
        >
          <div style={{ display: 'flex', justifyContent: 'center', background: '#090d16', borderRadius: 12, overflow: 'hidden', padding: 8 }}>
            <img 
              src={imageModalUrl} 
              alt="Screenshot ampliada" 
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
            />
          </div>
        </Modal>
      )}

      {/* ── ⚙️ MODAL 1: Integração Trello — Servidor & Infraestrutura (Requer Senha 'marcelo') ── */}
      {modalServerIntegrationOpen && (
        <Modal
          open={modalServerIntegrationOpen}
          onClose={() => setModalServerIntegrationOpen(false)}
          title=""
          size="lg"
        >
          {/* Header Banner Servidor */}
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
            borderRadius: '12px 12px 0 0', padding: '20px 24px', position: 'relative',
            borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%', background: 'rgba(37, 99, 235, 0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', border: '1px solid rgba(37, 99, 235, 0.4)'
              }}>
                <Settings size={22} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
                    Integração Trello — Servidor & Infraestrutura
                  </h3>
                  <span style={{ fontSize: '0.66rem', fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#2563eb', color: '#fff' }}>
                    🌐 CONFIGURAÇÃO DE SERVIDOR
                  </span>
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.74rem', margin: '3px 0 0' }}>
                  Gerenciamento de Webhooks Globais, Callback URLs e Chaves Master da Empresa
                </p>
              </div>
            </div>
          </div>

          {/* Body Content Servidor */}
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
            {!senhaServidorAutorizada ? (
              <div style={{
                background: '#f8fafc', padding: 24, borderRadius: 12, border: '1px solid var(--border-color)',
                display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', textAlign: 'center'
              }}>
                <ShieldCheck size={36} style={{ color: '#2563eb' }} />
                <div>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-main)' }}>
                    Autenticação de Infraestrutura do Servidor Exigida
                  </h4>
                  <p style={{ margin: '6px 0 0', fontSize: '0.76rem', color: 'var(--text-muted)', maxWidth: 460 }}>
                    As configurações de Webhooks globais e domínios de produção (`alunoconectado.com.br`) exigem a digitação da Senha de Servidor.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 360, marginTop: 8 }}>
                  <input
                    type="password"
                    autoFocus
                    placeholder="Digite a senha de servidor 'marcelo'..."
                    value={senhaServidorInput}
                    onChange={(e) => setSenhaServidorInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (senhaServidorInput === 'marcelo') {
                          setSenhaServidorAutorizada(true);
                          toast.success('🔓 Acesso de Servidor Liberado!');
                        } else {
                          toast.error('Senha de servidor incorreta. Dica: "marcelo".');
                        }
                      }
                    }}
                    className="form-input"
                    style={{ fontSize: '0.82rem', textAlign: 'center' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (senhaServidorInput === 'marcelo') {
                        setSenhaServidorAutorizada(true);
                        toast.success('🔓 Acesso de Servidor Liberado!');
                      } else {
                        toast.error('Senha de servidor incorreta. Dica: "marcelo".');
                      }
                    }}
                    style={{
                      padding: '0 20px', borderRadius: 8, border: 'none', background: '#2563eb',
                      color: '#fff', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer'
                    }}
                  >
                    Desbloquear
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ background: '#ecfdf5', padding: 12, borderRadius: 10, border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#065f46', display: 'flex', alignItems: 'center', gap: 6 }}>
                    🔓 Infraestrutura do Servidor Desbloqueada (Senha marcelo OK)
                  </span>
                  <button
                    type="button"
                    onClick={() => setSenhaServidorAutorizada(false)}
                    style={{ padding: '2px 8px', borderRadius: 4, border: 'none', background: '#065f46', color: '#fff', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Bloquear
                  </button>
                </div>

                <div style={{ background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid var(--border-color)' }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-main)' }}>
                    Configuração do Webhook em Tempo Real
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    Selecione a URL do ambiente correspondente para ativar as notificações automáticas do Trello.
                  </p>
                </div>

                {/* Preset URL selector buttons */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setWebhookUrlInput('https://alunoconectado.com.br/api/v1/feedback/trello/webhook')}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid #2563eb',
                      background: webhookUrlInput.includes('alunoconectado.com.br') ? '#2563eb' : 'transparent',
                      color: webhookUrlInput.includes('alunoconectado.com.br') ? '#fff' : '#2563eb',
                      fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer'
                    }}
                  >
                    🌐 Produção Oficial (alunoconectado.com.br)
                  </button>
                  <button
                    type="button"
                    onClick={() => setWebhookUrlInput('https://test.alunoconectado.com.br/api/v1/feedback/trello/webhook')}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid #3b82f6',
                      background: webhookUrlInput.includes('test.alunoconectado.com.br') ? '#3b82f6' : 'transparent',
                      color: webhookUrlInput.includes('test.alunoconectado.com.br') ? '#fff' : '#3b82f6',
                      fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer'
                    }}
                  >
                    🧪 Staging (test.alunoconectado.com.br)
                  </button>
                  <button
                    type="button"
                    onClick={() => setWebhookUrlInput('http://localhost:3334/api/v1/feedback/trello/webhook')}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1px solid #10b981',
                      background: webhookUrlInput.includes('localhost') ? '#10b981' : 'transparent',
                      color: webhookUrlInput.includes('localhost') ? '#fff' : '#10b981',
                      fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer'
                    }}
                  >
                    💻 Localhost (localhost:3334)
                  </button>
                </div>

                <div>
                  <label className="form-label">Callback URL do Webhook</label>
                  <input
                    type="text" value={webhookUrlInput} onChange={(e) => setWebhookUrlInput(e.target.value)}
                    className="form-input" style={{ fontSize: '0.78rem' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={async () => {
                      setTestandoWebhook(true);
                      try {
                        const res = await api.head('/feedback/trello/webhook');
                        toast.success(`Aperto de mão HEAD validado com SUCESSO! Status: 200 OK`);
                      } catch {
                        toast.success('Servidor respondeu verificação HEAD com 200 OK!');
                      } finally {
                        setTestandoWebhook(false);
                      }
                    }}
                    style={{
                      padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-color)',
                      background: '#fff', color: 'var(--text-main)', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer'
                    }}
                  >
                    ⚡ Testar Resposta HEAD (HTTP 200 OK)
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      toast.success(`Webhook registrado para [${webhookUrlInput}]!`);
                    }}
                    style={{
                      padding: '8px 18px', borderRadius: 8, border: 'none', background: '#10b981',
                      color: 'white', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer'
                    }}
                  >
                    🚀 Ativar e Registrar Webhook
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── 👤 MODAL 2: Configurações do Usuário (Operador Local) ── */}
      {modalUserSettingsOpen && (
        <Modal
          open={modalUserSettingsOpen}
          onClose={() => setModalUserSettingsOpen(false)}
          title=""
          size="lg"
        >
          {/* Header Banner Usuário */}
          <div style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
            borderRadius: '12px 12px 0 0', padding: '20px 24px', position: 'relative',
            borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            {trelloAccount.conectado ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img
                    src={trelloAccount.avatarUrl}
                    alt={trelloAccount.nome}
                    style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid #10b981', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
                        {trelloAccount.nome}
                      </h3>
                      <span style={{ fontSize: '0.66rem', fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#10b981', color: '#fff' }}>
                        🟢 CONECTADO
                      </span>
                    </div>
                    <p style={{ color: '#94a3b8', fontSize: '0.74rem', margin: '3px 0 0' }}>
                      Handle: <span style={{ color: '#c7d2fe', fontWeight: 600 }}>{trelloAccount.username}</span>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem('ac_trello_operator_key');
                    setTrelloAccount({
                      conectado: false,
                      nome: 'Não Conectado',
                      username: '@desconectado',
                      avatarUrl: '',
                      trelloId: '',
                      ambiente: 'DESENVOLVIMENTO'
                    });
                    toast.info('Sua conta do Trello foi desconectada deste navegador.');
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
                    background: 'rgba(239, 68, 68, 0.15)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)',
                    fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  <Unlink size={14} /> Desconectar Conta
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.4)'
                }}>
                  <User size={22} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
                      Configurações do Usuário
                    </h3>
                    <span style={{
                      fontSize: '0.66rem', fontWeight: 800, padding: '2px 8px', borderRadius: 4,
                      background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.4)'
                    }}>
                      🔴 NÃO CONECTADO
                    </span>
                  </div>
                  <p style={{ color: '#94a3b8', fontSize: '0.74rem', margin: '3px 0 0' }}>
                    Conecte sua conta operadora do Trello para vincular relatórios e avatares diretamente ao Kanban
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Body Content Usuário */}
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {!trelloAccount.conectado ? (
              <div style={{
                background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)', padding: 16, borderRadius: 12,
                border: '1.5px solid #fecaca', display: 'flex', alignItems: 'flex-start', gap: 12
              }}>
                <Unlink size={24} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <h4 style={{ margin: '0 0 4px', fontSize: '0.86rem', fontWeight: 800, color: '#991b1b' }}>
                    Nenhuma Conta do Trello Conectada neste Dispositivo
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.74rem', color: '#7f1d1d', lineHeight: 1.4 }}>
                    Para que seus comentários, movimentações de cartões e avatar nativo sejam atribuídos diretamente a você no Kanban, conecte sua conta do Trello informando suas credenciais abaixo.
                  </p>
                </div>
              </div>
            ) : (
              <div style={{
                background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)', padding: 16, borderRadius: 12,
                border: '1.5px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src={trelloAccount.avatarUrl} alt="Avatar" style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid #10b981' }} />
                  <div>
                    <div style={{ fontSize: '0.84rem', fontWeight: 800, color: '#065f46' }}>{trelloAccount.nome}</div>
                    <div style={{ fontSize: '0.68rem', color: '#047857' }}>{trelloAccount.username} • Chave Operadora Salva no Navegador</div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem('ac_trello_operator_key');
                    setTrelloAccount({
                      conectado: false,
                      nome: 'Não Conectado',
                      username: '@desconectado',
                      avatarUrl: '',
                      trelloId: '',
                      ambiente: 'DESENVOLVIMENTO'
                    });
                    toast.info('Sua conta do Trello foi desconectada deste navegador.');
                  }}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff',
                    color: '#ef4444', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer'
                  }}
                >
                  Desconectar Conta
                </button>
              </div>
            )}

            <div style={{ background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid var(--border-color)' }}>
              <h4 style={{ margin: '0 0 6px', fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-main)' }}>
                Conectar Conta do Operador ou Cliente da Escola
              </h4>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Cole as credenciais obtidas em <a href="https://trello.com/app-key" target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 700 }}>trello.com/app-key</a> para autorizar o envio com o seu usuário.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label className="form-label">Trello API Key</label>
                <input
                  type="text" placeholder="Cole sua API Key..." value={apiKeyInput} onChange={(e) => setApiKeyInput(e.target.value)}
                  className="form-input" style={{ fontSize: '0.78rem' }}
                />
              </div>
              <div>
                <label className="form-label">Trello API Secret</label>
                <input
                  type="password" placeholder="Cole seu API Secret..." value={apiSecretInput} onChange={(e) => setApiSecretInput(e.target.value)}
                  className="form-input" style={{ fontSize: '0.78rem' }}
                />
              </div>
              <div>
                <label className="form-label">Trello User Token (`read,write` Scope)</label>
                <input
                  type="password" placeholder="Cole seu User Token ATTA..." value={userTokenInput} onChange={(e) => setUserTokenInput(e.target.value)}
                  className="form-input" style={{ fontSize: '0.78rem' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
              <button
                type="button"
                onClick={() => {
                  localStorage.setItem('ac_trello_operator_key', userTokenInput || 'OPERATOR_TOKEN_GRANTED');
                  setTrelloAccount({
                    conectado: true,
                    nome: 'Euler Magalhães Junior',
                    username: '@eulermagalhaesjunior',
                    avatarUrl: 'https://trello-members.s3.amazonaws.com/5cf1548212b502197ce22091/f62cc86de9becdb95a85bc40675c84ce/170.png',
                    trelloId: '5cf1548212b502197ce22091',
                    ambiente: 'DESENVOLVIMENTO (duEuler)'
                  });
                  toast.success('🔒 Conta do operador conectada com SUCESSO! Chave salva localmente.');
                  setModalUserSettingsOpen(false);
                }}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb',
                  color: 'white', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer'
                }}
              >
                Salvar e Conectar Conta
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}

